
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface ReportParams {
  CalledFromELW: string;
  Category:      string;
  StuRepNo:      string;
  StuSch:        string;
  StuFam:        string;
  StudentId:     string;
  StudyYear:     string;
  COUNTRY:       string;
  SurName:       string;
  FirstName:     string;
}

type LoadState = "idle" | "loading" | "success" | "error";


const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const REPORT_ENDPOINT = `${API_BASE_URL}api/md-ereport-pdf/`;

/** Allowed admin categories — mirrors VB: If LoginCategory = "A" Or "DA" Or "SA" */
const ALLOWED_CATEGORIES = new Set(["A", "DA", "SA"]);

function getClientHeight(): number {
  return (
    window.innerHeight ||
    document.documentElement?.clientHeight ||
    document.body?.clientHeight ||
    600
  );
}

function parseQueryParams(): ReportParams {
  const qs = new URLSearchParams(window.location.search);
  return {
    CalledFromELW: qs.get("CalledFromELW") ?? "",
    Category:      qs.get("Category")      ?? "",
    StuRepNo:      qs.get("StuRepNo")      ?? "0",
    StuSch:        qs.get("StuSch")        ?? "",
    StuFam:        qs.get("StuFam")        ?? "",
    StudentId:     qs.get("StudentId")     ?? "",
    StudyYear:     qs.get("StudyYear")     ?? "0",
    COUNTRY:       qs.get("COUNTRY")       ?? "",
    SurName:       qs.get("SurName")       ?? "",
    FirstName:     qs.get("FirstName")     ?? "",
  };
}

/** Build the full API URL from parsed params. */
function buildApiUrl(p: ReportParams): string {
  const qs = new URLSearchParams();
  (Object.keys(p) as Array<keyof ReportParams>).forEach((k) => {
    if (p[k] !== "") qs.set(k, p[k]);
  });
  return `${REPORT_ENDPOINT}?${qs.toString()}`;
}

const MDEReportPDF: React.FC = () => {
  const params     = useRef<ReportParams>(parseQueryParams());
  const blobUrlRef = useRef<string | null>(null);

  const [loadState,  setLoadState]  = useState<LoadState>("idle");
  const [errorMsg,   setErrorMsg]   = useState<string>("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [iframeH,    setIframeH]    = useState<number>(getClientHeight() - 35);

  const handleResize = useCallback(() => {
    setIframeH(getClientHeight() - 35);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const validateParams = useCallback((): string | null => {
    const p = params.current;
    if (!ALLOWED_CATEGORIES.has(p.Category.toUpperCase())) {
      return `Access denied: Category "${p.Category}" is not permitted. ` +
             `Only A, DA, or SA may generate this report.`;
    }
    if (!p.StuSch)    return "Missing required parameter: StuSch (SchoolCode).";
    if (!p.StudentId) return "Missing required parameter: StudentId (Login).";
    if (!p.StudyYear || p.StudyYear === "0")
                      return "Missing required parameter: StudyYear.";
    return null;
  }, []);

  const fetchReport = useCallback(async () => {
    const validationError = validateParams();
    if (validationError) {
      setLoadState("error");
      setErrorMsg(validationError);
      return;
    }

    setLoadState("loading");
    setErrorMsg("");

    // Revoke previous blob URL to avoid memory leaks
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      setPdfBlobUrl(null);
    }

    try {
      const url = buildApiUrl(params.current);

      const response = await fetch(url, {
        method:      "GET",
        credentials: "include",      // forward session cookie (Django auth)
        headers: {
          Accept: "application/pdf",
        },
      });

      // Surface server-side error messages clearly
      if (!response.ok) {
        let serverMsg = `HTTP ${response.status} ${response.statusText}`;
        try {
          const text = await response.text();
          if (text) serverMsg += `: ${text}`;
        } catch (_) { /* ignore */ }
        throw new Error(serverMsg);
      }

      const contentType = response.headers.get("Content-Type") ?? "";
      if (!contentType.includes("application/pdf")) {
        const text = await response.text().catch(() => "(unreadable body)");
        throw new Error(
          `Expected application/pdf but received "${contentType}". Body: ${text}`
        );
      }

      // Stream to Blob → Object URL  (replaces original Response.Redirect)
      const blob   = await response.blob();
      const objUrl = URL.createObjectURL(blob);
      blobUrlRef.current = objUrl;

      setPdfBlobUrl(objUrl);
      setLoadState("success");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLoadState("error");
      setErrorMsg(msg);
    }
  }, [validateParams]);

  // Trigger on mount (mirrors original ASPX which ran everything in Page_Load)
  useEffect(() => {
    fetchReport();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [fetchReport]);

  return (
    <div style={styles.page}>

      {/* ── Loading ── */}
      {loadState === "loading" && (
        <div style={styles.centred}>
          <Spinner />
          <p style={styles.loadingText}>Generating Knowledge Gap Report…</p>
          <p style={styles.subText}>
            Student: <strong>{params.current.FirstName || params.current.StudentId}</strong>
            &nbsp;| Year: <strong>{params.current.StudyYear}</strong>
            &nbsp;| Report #: <strong>{params.current.StuRepNo}</strong>
          </p>
        </div>
      )}

      {/* ── Error ── */}
      {loadState === "error" && (
        <div style={styles.centred}>
          <div style={styles.errorCard}>
            <div style={styles.errorIcon}>⚠</div>
            <h2 style={styles.errorTitle}>Report Generation Failed</h2>
            <p style={styles.errorBody}>{errorMsg}</p>
            <div style={styles.buttonRow}>
              <button style={styles.btnPrimary} onClick={fetchReport}>
                ↺ Retry
              </button>
              <button
                style={styles.btnSecondary}
                onClick={() => window.history.back()}
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PDF iframe — mirrors <iframe id="ifrmPDF"> in original ASPX ── */}
      {loadState === "success" && pdfBlobUrl && (
        <iframe
          id="ifrmPDF"
          src={pdfBlobUrl}
          title="Knowledge Gap Report"
          style={{ ...styles.iframe, height: `${iframeH}px` }}
        />
      )}
    </div>
  );
};

const Spinner: React.FC = () => (
  <div style={spinnerStyle} />
);

const styles: Record<string, React.CSSProperties> = {
  page: {
    position:   "relative",
    width:      "100%",
    height:     "100vh",
    margin:     0,
    padding:    0,
    boxSizing:  "border-box",
    background: "#f5f5f0",
    fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif",
  },
  centred: {
    position:        "absolute",
    inset:           0,
    display:         "flex",
    flexDirection:   "column",
    alignItems:      "center",
    justifyContent:  "center",
    gap:             12,
  },
  loadingText: {
    fontSize:    16,
    fontWeight:  600,
    color:       "#333",
    margin:      0,
  },
  subText: {
    fontSize: 13,
    color:    "#666",
    margin:   0,
  },
  errorCard: {
    background:   "#fff",
    borderRadius: 12,
    padding:      "40px 48px",
    maxWidth:     560,
    width:        "90%",
    textAlign:    "center",
    boxShadow:    "0 4px 24px rgba(0,0,0,0.10)",
    border:       "1px solid #fde8e8",
  },
  errorIcon: {
    fontSize:    48,
    color:       "#e53e3e",
    marginBottom: 8,
  },
  errorTitle: {
    fontSize:    20,
    fontWeight:  700,
    color:       "#c53030",
    margin:      "0 0 12px",
  },
  errorBody: {
    fontSize:    14,
    color:       "#555",
    lineHeight:  1.6,
    margin:      "0 0 24px",
    wordBreak:   "break-word",
  },
  buttonRow: {
    display:        "flex",
    gap:            12,
    justifyContent: "center",
  },
  btnPrimary: {
    padding:         "10px 24px",
    fontSize:        14,
    fontWeight:      600,
    backgroundColor: "#e67e22",   // MD orange
    color:           "#fff",
    border:          "none",
    borderRadius:    8,
    cursor:          "pointer",
  },
  btnSecondary: {
    padding:         "10px 24px",
    fontSize:        14,
    fontWeight:      600,
    backgroundColor: "#fff",
    color:           "#555",
    border:          "1px solid #ccc",
    borderRadius:    8,
    cursor:          "pointer",
  },
  iframe: {
    display: "block",
    width:   "100%",
    border:  "none",
  },
};

const spinnerStyle: React.CSSProperties = {
  width:        52,
  height:       52,
  borderRadius: "50%",
  border:       "5px solid #eee",
  borderTop:    "5px solid #e67e22",
  animation:    "mde-spin 0.85s linear infinite",
};

// Inject @keyframes once
if (typeof document !== "undefined" && !document.getElementById("mde-spin-style")) {
  const s = document.createElement("style");
  s.id = "mde-spin-style";
  s.textContent = "@keyframes mde-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(s);
}

export default MDEReportPDF;